import React from 'react';
import { Link } from 'react-router-dom';

// "Since you started": the read under each supplement (2026-10 restyle).
// One sentence, then a line per number we checked: a dot (green better, red
// worse, gold moved with no better direction, grey about the same), the
// name, and what happened. Nothing here phrases a verdict of its own; the
// server built the words from the numbers.

const DOT = {
  better: 'bg-good-strong dark:bg-good',
  worse: 'bg-off-strong dark:bg-off',
  neutral: 'bg-you',
  quiet: 'bg-fg-3',
};

const dotFor = (r) => {
  if (r.verdict !== 'moved') return DOT.quiet;
  if (r.is_better === true) return DOT.better;
  if (r.is_better === false) return DOT.worse;
  return DOT.neutral;
};

const shortLabel = (label) => (label || '').split(' (')[0];

const lineFor = (r) => {
  if (r.verdict === 'moved') return <>{r.before_text} → {r.window_text}</>;
  if (r.verdict === 'no_change') return <span className="text-fg-3">about the same</span>;
  if (r.verdict === 'too_early') return <span className="text-fg-3">too early to say</span>;
  return <span className="text-fg-3">not enough days</span>;
};

// Day N of M, for the bar while a read is still too early.
const progressOf = (read) => {
  const m = /day (\d+) of (\d+)/i.exec(read.headline || '');
  if (!m) return null;
  return { n: Number(m[1]), of: Number(m[2]) };
};

export const readSentence = (read) => {
  const outcomes = read?.outcomes || [];
  const moved = outcomes.filter((r) => r.verdict === 'moved');
  const tooEarly = outcomes.length > 0 && outcomes.every((r) => r.verdict === 'too_early');
  if (tooEarly) {
    const p = progressOf(read);
    return p ? `Too early. First read in ${Math.max(1, p.of - p.n)} day${p.of - p.n === 1 ? '' : 's'}.` : 'Too early to read.';
  }
  if (moved.length === 0) return outcomes.some((r) => r.verdict === 'no_change') ? 'Nothing moved. That’s a result too.' : (read?.headline || '');
  if (moved.length === 1) {
    const r = moved[0];
    return `One thing moved. ${shortLabel(r.label)} is ${r.direction === 'up' ? 'higher' : 'lower'}.`;
  }
  return `${moved.length} things moved.`;
};

const SinceYouStarted = ({ read }) => {
  if (!read) return null;
  const outcomes = read.outcomes || [];
  const tooEarly = outcomes.length > 0 && outcomes.every((r) => r.verdict === 'too_early');
  const p = tooEarly ? progressOf(read) : null;
  const hasObservation = outcomes.some((r) => r.observation_id);

  if (tooEarly) {
    return (
      <div className="mt-1">
        {p && (
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-line" aria-label={`${p.n} of ${p.of} days`}>
            <i className="block h-full rounded-full bg-fg" style={{ width: `${Math.min(100, (p.n / p.of) * 100)}%` }} />
          </div>
        )}
        <p className="mt-3 text-sm text-fg-3">We compare the days since you started with the same number of days before. Keep syncing as usual.</p>
      </div>
    );
  }

  return (
    <div className="mt-2.5 flex flex-col gap-1.5">
      {outcomes.map((r) => (
        <div key={r.field} className="flex items-center gap-2.5 text-base text-fg-2">
          <i className={`h-2 w-2 flex-none rounded-full ${dotFor(r)}`} aria-hidden="true" />
          <b className="font-medium text-fg">{shortLabel(r.label)}</b>
          <span>{lineFor(r)}</span>
        </div>
      ))}
      {hasObservation && (
        <Link to="/insights" className="mt-1 text-sm font-medium text-fg-2 hover:text-fg">See the observation →</Link>
      )}
    </div>
  );
};

export default SinceYouStarted;
